import { useState, useEffect } from "react";
import { useRouter, useParams } from "next/navigation";
import Image from "next/image";
import {
  ChevronRight,
  Lock,
  Gift,
  Package,
  Truck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth.store";
import { authFetch, isLoggedIn } from "@/lib/auth-client";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ClaimPrizeModal } from "@/components/events/ClaimPrizeModal";
import { PrizesShowcase } from "@/components/events/PrizeDisplay";
import { scheduleMainEventNotifications } from "@/lib/notifications";
import type {
  MainEventDetail,
  UndercardSummary,
  MyEntryDetail,
  LeaderboardEntry,
  PrizeClaimData,
} from "@/services/events/main-event";
import { RoundStepper } from "@/components/events/RoundStepper";
import { asset } from "@/lib/asset";
import { getPrizeForRank } from "@/lib/prize";

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  UPCOMING: { bg: "bg-blue-500/10 border-blue-500/30", text: "text-blue-400" },
  OPEN: { bg: "bg-verdigris/10 border-verdigris/30", text: "text-verdigris" },
  LOCKED: { bg: "bg-gold/10 border-gold/30", text: "text-gold" },
  SCORING: {
    bg: "bg-orange-500/10 border-orange-500/30",
    text: "text-orange-400",
  },
  COMPLETED: { bg: "bg-tertiary/10 border-tertiary/30", text: "text-tertiary" },
};

function UndercardRow({
  undercard,
  myEntry,
  onClick,
}: {
  undercard: UndercardSummary;
  myEntry: MyEntryDetail | null;
  onClick: () => void;
}) {
  const statusStyle = STATUS_COLORS[undercard.status] || STATUS_COLORS.UPCOMING;
  const isLocked =
    undercard.status === "LOCKED" ||
    undercard.status === "SCORING" ||
    undercard.status === "COMPLETED";

  const myUndercardEntry = myEntry?.undercardEntries.find(
    (ue) => ue.undercardId === undercard.id,
  );

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 rounded-xl bg-surface-2 border border-surface-3 hover:border-gold/30 transition-colors group"
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="flex w-full justify-between">
          <div className="flex flex-col items-start gap-2 mb-1 min-w-0">
            <h3 className="text-cream font-serif font-bold  min-w-0 basis-0">
              {undercard.title}
            </h3>
            <div className="flex items-center gap-2 sm:gap-4 text-xs text-warm-gray flex-wrap">
              <span>{undercard.gameCount} games</span>
              <span>{undercard.pointsPerCorrectPick} points/pick</span>
              {undercard.propCount > 0 && (
                <span>{undercard.propCount} props</span>
              )}
              {myUndercardEntry && (
                <span className="text-gold">
                  {myUndercardEntry.picksSubmitted} picks ·{" "}
                  {myUndercardEntry.totalScore} points
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-center items-center">
            <span
              className={cn(
                "px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border shrink-0",
                statusStyle.bg,
                statusStyle.text,
              )}
            >
              {undercard.status}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isLocked && <Lock className="w-4 h-4 text-tertiary" />}
          <ChevronRight className="w-5 h-5 text-tertiary group-hover:text-gold transition-colors" />
        </div>
      </div>
    </button>
  );
}

export default function MainEventDetailPage() {
  const router = useRouter();
  const { accessToken } = useAuthStore();
  const [event, setEvent] = useState<MainEventDetail | null>(null);
  const [myEntry, setMyEntry] = useState<MyEntryDetail | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [entering, setEntering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Migrated from Next.js: original code awaited `params` (a Promise from
  // App Router) inside a useEffect. React Router exposes :id synchronously,
  // so we read it directly via useParams.
  const { id: eventIdParam } = useParams<{ id: string }>();
  const [eventId, setEventId] = useState<string>(eventIdParam ?? "");
  const [accessCode, setAccessCode] = useState("");
  const [myClaims, setMyClaims] = useState<PrizeClaimData[]>([]);
  const [claimModal, setClaimModal] = useState<PrizeClaimData | null>(null);

  console.log(event?.imageUrl);

  useEffect(() => {
    if (eventIdParam) setEventId(eventIdParam);
  }, [eventIdParam]);

  useEffect(() => {
    if (!eventId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        // Reset per-event state so stale entry/claims (or a 404 on my-entry)
        // from a previously viewed event can't drive notifications for this one.
        setMyEntry(null);
        setMyClaims([]);
        const [eventRes, lbRes] = await Promise.all([
          fetch(`${API_BASE}/api/main-events/${eventId}`),
          fetch(`${API_BASE}/api/main-events/${eventId}/leaderboard?limit=10`),
        ]);

        if (eventRes.ok) setEvent(await eventRes.json());
        else setError("Event not found");

        if (lbRes.ok) setLeaderboard(await lbRes.json());

        // Fetch user entry + prize claims if logged in
        if (isLoggedIn()) {
          const entryRes = await authFetch(
            `/api/main-events/${eventId}/my-entry`,
          );
          if (entryRes.ok) setMyEntry(await entryRes.json());

          const claimsRes = await authFetch(
            `/api/main-events/${eventId}/my-claims`,
          );
          if (claimsRes.ok) setMyClaims(await claimsRes.json());
        }
      } catch {
        setError("Failed to load event");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [eventId, accessToken]);

  useEffect(() => {
    // Wait for this event's data to load and confirm it matches the route —
    // otherwise stale entry/claims from a previously viewed event would
    // schedule notifications for the wrong one.
    if (!event || loading || event.id !== eventId) return;
    const sdk = typeof window !== "undefined" ? window.JestSDK : null;
    const wonPrize =
      event.status === "COMPLETED" &&
      !!myEntry?.rank &&
      getPrizeForRank(event.prizeDistribution, myEntry.rank) > 0;

    // Per-undercard "have they made picks yet?" map — feeds the progress-
    // based rescheduling so we don't badger users who already locked their
    // selections. An undercard with picksSubmitted > 0 counts as "done".
    const picksByUndercard: Record<string, boolean> = {};
    for (const uc of myEntry?.undercardEntries ?? []) {
      picksByUndercard[uc.undercardId] = uc.picksSubmitted > 0;
    }

    void scheduleMainEventNotifications(sdk, event, {
      hasEntry: !!myEntry,
      wonPrize,
      unclaimedPrize: myClaims.some((claim) => claim.status === "UNCLAIMED"),
      picksByUndercard,
    });
  }, [event, myEntry, myClaims, loading, eventId]);

  const handleEnter = async () => {
    if (!isLoggedIn()) {
      router.push("/login");
      return;
    }
    try {
      setEntering(true);
      setError(null);
      const body =
        event?.accessType === "CODE_REQUIRED" ? { accessCode } : undefined;
      const res = await authFetch(`/api/main-events/${eventId}/enter`, {
        method: "POST",
        ...(body
          ? {
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body),
            }
          : {}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to enter");
      // Refresh entry
      const entryRes = await authFetch(`/api/main-events/${eventId}/my-entry`);
      if (entryRes.ok) setMyEntry(await entryRes.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to enter");
    } finally {
      setEntering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-warm-gray">Event not found</p>
      </div>
    );
  }

  const isJoinable = event.status === "UPCOMING" || event.status === "OPEN";

  return (
    <div className="min-h-screen bg-surface">
      {/* Sticky Round Stepper */}
      {event.undercards.length > 1 && (
        <RoundStepper
          rounds={event.undercards.map((uc) => ({
            id: uc.id,
            title: uc.title,
            status: uc.status,
            round: uc.round,
          }))}
          eventId={eventId}
          onNavigate={(id) =>
            router.push(`/events/main/${eventId}/undercards/${id}`)
          }
        />
      )}

      <div className="ornamental-rule max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={() => router.push("/events")}
            className="text-sm text-tertiary hover:text-cream mb-4 inline-block"
          >
            ← Back to Events
          </button>
          <div className="mb-6 rounded-xl overflow-hidden border border-surface-3">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={event.imageUrl || `${API_BASE}/api/og/event/${eventId}`}
              alt={event.title}
              className="w-full h-auto"
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 sm:gap-3 text-sm text-warm-gray flex-wrap">
                <span
                  className={cn(
                    "px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider border",
                    (STATUS_COLORS[event.status] || STATUS_COLORS.UPCOMING).bg,
                    (STATUS_COLORS[event.status] || STATUS_COLORS.UPCOMING)
                      .text,
                  )}
                >
                  {event.status}
                </span>
                <span>{event.entryCount} entered</span>
                {event.accessType === "CODE_REQUIRED" && (
                  <span className="text-gold">Code required</span>
                )}
              </div>
            </div>
            {!myEntry && isJoinable && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                {event.accessType === "CODE_REQUIRED" && (
                  <input
                    type="text"
                    placeholder="Enter access code"
                    value={accessCode}
                    onChange={(e) =>
                      setAccessCode(e.target.value.toUpperCase())
                    }
                    className="px-3 py-2 rounded-lg bg-surface-2 border border-surface-3 text-cream placeholder-warm-gray/50 focus:border-gold focus:outline-none font-mono text-sm w-full sm:w-48"
                  />
                )}
                <Button
                  variant="coins"
                  size="lg"
                  onClick={handleEnter}
                  disabled={
                    entering ||
                    (event.accessType === "CODE_REQUIRED" && !accessCode.trim())
                  }
                  className="w-full sm:w-auto"
                >
                  {entering
                    ? "Entering..."
                    : event.entryFee === 0
                      ? "Enter Event"
                      : `Enter (${event.entryFee} coins)`}
                </Button>
              </div>
            )}
            {myEntry && (
              <div className="text-left sm:text-right">
                <p className="text-xs text-tertiary uppercase tracking-wider">
                  Your Score
                </p>
                <p className="text-2xl font-bold text-gold">
                  {myEntry.totalScore}
                </p>
                {myEntry.rank && (
                  <p className="text-xs text-warm-gray">Rank #{myEntry.rank}</p>
                )}
              </div>
            )}
          </div>
        </div>

        <PrizesShowcase
          event={event}
          coinIconSrc={asset("assets/icons/coins.webp")}
        />

        {error && (
          <div className="p-4 mb-6 bg-oxblood/10 border border-oxblood/30 rounded-xl text-oxblood text-sm">
            {error}
          </div>
        )}

        {/* Winner/Placement Banner */}
        {event.status === "COMPLETED" &&
          myEntry &&
          myEntry.rank &&
          (() => {
            const prize = getPrizeForRank(
              event.prizeDistribution,
              myEntry.rank,
            );
            return (
              <div
                className={cn(
                  "mb-6 p-4 sm:p-5 rounded-xl flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4",
                  prize > 0
                    ? "bg-verdigris/10 border border-verdigris/30"
                    : "bg-surface-2 border border-surface-3",
                )}
              >
                <Image
                  src={asset("assets/icons/trophy.webp")}
                  alt=""
                  width={32}
                  height={32}
                  className="flex-shrink-0"
                />
                <div className="flex-1">
                  <p
                    className={cn(
                      "font-serif font-bold text-lg",
                      prize > 0 ? "text-verdigris" : "text-cream",
                    )}
                  >
                    You finished #{myEntry.rank}!
                  </p>
                  {prize > 0 ? (
                    <p className="text-sm text-warm-gray flex items-center gap-1">
                      Won
                      <Image
                        src={asset("assets/icons/coins.webp")}
                        alt=""
                        width={14}
                        height={14}
                      />
                      <span className="font-bold text-gold">
                        {prize.toLocaleString()}
                      </span>
                      coins
                    </p>
                  ) : (
                    <p className="text-sm text-warm-gray">
                      Scored {myEntry.totalScore} points
                    </p>
                  )}
                </div>
              </div>
            );
          })()}

        {/* Item Prize Claims */}
        {myClaims.length > 0 &&
          myClaims.map((claim) => (
            <div
              key={claim.id}
              className={cn(
                "mb-4 p-4 sm:p-5 rounded-xl flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4",
                claim.status === "UNCLAIMED"
                  ? "bg-gold/10 border border-gold/30"
                  : claim.status === "SHIPPED"
                    ? "bg-verdigris/10 border border-verdigris/30"
                    : "bg-surface-2 border border-surface-3",
              )}
            >
              {claim.itemImageUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={claim.itemImageUrl}
                  alt=""
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <Gift className="w-8 h-8 text-gold flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-serif font-bold text-cream break-words">
                  {claim.status === "UNCLAIMED"
                    ? `You won: ${claim.itemName}!`
                    : claim.itemName}
                </p>
                <p className="text-sm text-warm-gray flex items-center gap-1">
                  {claim.status === "UNCLAIMED" &&
                    "Claim your prize by submitting shipping info"}
                  {claim.status === "CLAIMED" && (
                    <>
                      <Package className="w-3.5 h-3.5 text-gold" /> Processing —
                      we&apos;ll notify you when it ships
                    </>
                  )}
                  {claim.status === "SHIPPED" && (
                    <>
                      <Truck className="w-3.5 h-3.5 text-verdigris" /> Shipped
                      {claim.trackingNumber ? ` — ${claim.trackingNumber}` : ""}
                    </>
                  )}
                </p>
              </div>
              {claim.status === "UNCLAIMED" && (
                <Button
                  variant="coins"
                  className="w-full sm:w-auto shrink-0"
                  onClick={() => setClaimModal(claim)}
                >
                  Claim Prize
                </Button>
              )}
            </div>
          ))}

        {/* Claim Prize Modal */}
        <ClaimPrizeModal
          claim={
            claimModal
              ? {
                  id: claimModal.id,
                  mainEventId: claimModal.mainEventId,
                  itemName: claimModal.itemName,
                  itemImageUrl: claimModal.itemImageUrl,
                  rank: claimModal.rank,
                }
              : null
          }
          userEmail={useAuthStore.getState().user?.email || ""}
          isOpen={!!claimModal}
          onClose={() => setClaimModal(null)}
          onClaimed={async () => {
            const res = await authFetch(
              `/api/main-events/${eventId}/my-claims`,
            );
            if (res.ok) setMyClaims(await res.json());
          }}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Undercards */}
          <div className="lg:col-span-2 space-y-3">
            <span className="text-gold text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              Rounds
            </span>
            {event.undercards.map((undercard) => (
              <UndercardRow
                key={undercard.id}
                undercard={undercard}
                myEntry={myEntry}
                onClick={() =>
                  router.push(
                    `/events/main/${eventId}/undercards/${undercard.id}`,
                  )
                }
              />
            ))}
          </div>

          {/* Leaderboard */}
          <div>
            <span className="text-gold text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              Leaderboard
            </span>
            <div className="bg-surface-2 rounded-xl border border-surface-3 overflow-hidden">
              {leaderboard.length === 0 ? (
                <p className="p-4 text-sm text-tertiary text-center">
                  No entries yet
                </p>
              ) : (
                <div className="divide-y divide-surface-3">
                  {leaderboard.map((entry) => (
                    <div
                      key={entry.userId}
                      className="flex items-center justify-between px-4 py-3 hover:bg-surface-3/50 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={cn(
                            "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                            entry.rank === 1
                              ? "bg-gold/20 text-gold"
                              : entry.rank === 2
                                ? "bg-tertiary/20 text-cream"
                                : entry.rank === 3
                                  ? "bg-orange-500/20 text-orange-400"
                                  : "bg-surface-3 text-tertiary",
                          )}
                        >
                          {entry.rank}
                        </span>
                        <span className="text-sm text-cream group-hover:text-gold transition-colors">
                          {entry.username}
                        </span>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        <span className="text-sm font-bold text-gold">
                          {entry.totalScore}
                        </span>
                        {entry.prizeAmount && entry.prizeAmount > 0 && (
                          <p className="text-xs text-amber-400/70">
                            {entry.prizeAmount.toLocaleString()} coins
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
